import { getSupabaseClient, initializeSupabase } from './supabase';

export interface AuthUser {
  id: string;
  email: string;
  name?: string;
  avatar_url?: string;
  credits: number;
  language_preference: string;
  subscription_status: string;
  total_readings: number;
}

export interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
}

export type AuthProvider = 'google' | 'github' | 'discord' | 'facebook' | 'twitter';

class AuthService {
  private async getSupabase() {
    try {
      return getSupabaseClient();
    } catch {
      // If client not initialized, try to initialize it
      console.warn('⚠️ [AuthService] Supabase client not initialized, attempting to initialize...');
      await initializeSupabase();
      return getSupabaseClient();
    }
  }

  async signUp(email: string, password: string, name?: string) {
    try {
      const supabase = await this.getSupabase();
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: name || '',
          }
        }
      });

      if (error) throw error;

      // Create user profile via API
      if (data.user) {
        try {
          const response = await fetch(`/api/users/register`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              id: data.user.id,
              email: data.user.email!,
              name: name || '',
              credits: 1, // Welcome credit
              language_preference: 'zh'
            })
          });

          if (!response.ok) {
            const errorData = await response.json();
            console.error('Profile creation error:', errorData);
          }
        } catch (apiError) {
          console.error('Failed to create user profile via API:', apiError);
        }
      }

      return { data, error: null };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      return { data: null, error: errorMessage };
    }
  }

  async signIn(email: string, password: string) {
    try {
      // 通过后端代理进行密码登录，避免浏览器直连 Supabase Auth
      const resp = await fetch('/api/auth/password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const result = await resp.json();
      if (!resp.ok || !result?.success) {
        const msg = result?.error?.message || 'Authentication failed';
        throw new Error(msg);
      }

      const tokens = result.data;

      // 使用 supabase-js 注入会话，保持后续 API 与刷新逻辑一致
      const supabase = await this.getSupabase();
      const { data: sessionData, error: setErr } = await supabase.auth.setSession({
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token
      });
      if (setErr) throw setErr;

      return { data: sessionData, error: null };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      return { data: null, error: errorMessage };
    }
  }

  async signInWithProvider(provider: AuthProvider) {
    try {
      const supabase = await this.getSupabase();

      // 保存当前路由作为 returnUrl（除了首页和认证相关页面）
      const currentPath = window.location.pathname;
      const shouldSaveReturnUrl = currentPath !== '/' &&
                                   !currentPath.startsWith('/auth/') &&
                                   currentPath !== '/birth-info';

      const returnUrl = shouldSaveReturnUrl ? currentPath : null;
      const redirectTo = returnUrl
        ? `${window.location.origin}/auth/callback?returnUrl=${encodeURIComponent(returnUrl)}`
        : `${window.location.origin}/auth/callback`;

      console.log('🔐 [AuthService] Signing in with provider:', provider);
      console.log('🔐 [AuthService] Current path:', currentPath);
      console.log('🔐 [AuthService] Return URL:', returnUrl);
      console.log('🔐 [AuthService] Redirect to:', redirectTo);

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo
        }
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      return { data: null, error: errorMessage };
    }
  }

  async signOut() {
    try {
      const supabase = await this.getSupabase();
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      return { error: null };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      return { error: errorMessage };
    }
  }

  /**
   * 获取当前用户信息 - 统一数据源重构版本
   * 集成用户基本信息和积分信息的获取，确保返回完整的用户对象
   */
  async getCurrentUser(): Promise<AuthUser | null> {
    let retryCount = 0;
    const maxRetries = 1; // 🔧 修复: 减少重试次数从 2 到 1，避免速率限制

    console.log('🔄 [getCurrentUser] Starting unified user data retrieval...');

    while (retryCount < maxRetries) {
      try {
        retryCount++;
        console.log(`🔄 [getCurrentUser] Attempt ${retryCount}/${maxRetries}`);

        // === Step 1: 验证Supabase会话状态 ===
        const supabaseClient = await this.getSupabase();
        const { data: { session }, error: sessionError } = await supabaseClient.auth.getSession();

        if (sessionError || !session?.user) {
          console.warn(`⚠️ [getCurrentUser] No valid session found (attempt ${retryCount}):`, sessionError?.message || 'No session');
          return null; // 🔧 修复: 立即返回，不再重试
        }

        console.log(`✅ [getCurrentUser] Valid session found for user: ${session.user.id}`);

        // === Step 2: 统一用户信息获取 (包含积分) ===
        try {
          console.log(`🌐 [getCurrentUser] Attempting unified user data fetch (attempt ${retryCount})...`);

          // 🔧 修复: 串行请求而非并行，减少同时请求数
          const profileResponse = await fetch('/api/users/profile', {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
            },
          });

          let profile = null;
          let credits = 0;

          // 🔧 修复: 处理用户信息响应（串行请求）
          if (profileResponse.ok) {
            const profileData = await profileResponse.json();
            profile = profileData.data || profileData;
            credits = profile?.credits || 0; // 🔧 直接从 profile 获取 credits
            console.log(`✅ [getCurrentUser] Profile API call successful, credits: ${credits}`);
          } else {
            console.warn(`⚠️ [getCurrentUser] Profile API call failed`);
          }

          if (profile) {
            const authUser: AuthUser = {
              id: profile.id,
              email: profile.email,
              name: profile.name || session.user.user_metadata?.name || '',
              avatar_url: profile.avatar_url || session.user.user_metadata?.avatar_url,
              credits: credits, // 使用统一获取的积分值
              language_preference: profile.language_preference || 'zh',
              subscription_status: profile.subscription_status || 'free',
              total_readings: profile.total_readings || 0
            };

            console.log(`🎉 [getCurrentUser] Unified data retrieval successful, credits: ${authUser.credits}`);
            return authUser;
          }

        } catch (apiError) {
          console.warn(`⚠️ [getCurrentUser] Unified API call failed (attempt ${retryCount}):`, apiError instanceof Error ? apiError.message : 'Unknown error');
        }

        // === Step 3: 最终fallback - 基础用户信息 ===
        console.log(`🚨 [getCurrentUser] Using basic user info (final fallback)`);

        const authUser: AuthUser = {
          id: session.user.id,
          email: session.user.email || '',
          name: session.user.user_metadata?.name || session.user.email?.split('@')[0] || '',
          avatar_url: session.user.user_metadata?.avatar_url,
          credits: 0,
          language_preference: 'zh',
          subscription_status: 'free',
          total_readings: 0
        };

        console.log(`⚠️ [getCurrentUser] Using basic fallback - user should refresh to get credits`);
        return authUser;
        
      } catch (error) {
        console.error(`❌ [getCurrentUser] Unexpected error (attempt ${retryCount}):`, error);
        
        if (retryCount >= maxRetries) {
          console.error(`❌ [getCurrentUser] All retry attempts exhausted, returning null`);
          return null;
        } else {
          console.log(`🔄 [getCurrentUser] Waiting 1000ms before retry due to unexpected error...`);
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }
    
    console.error(`❌ [getCurrentUser] Maximum retry attempts (${maxRetries}) reached, authentication failed`);
    return null;
  }


  async updateProfile(updates: Partial<AuthUser>) {
    try {
      const supabase = await this.getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No authenticated user');

      const response = await fetch('/api/users/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to update profile');
      }

      const { data } = await response.json();
      return { data, error: null };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      return { data: null, error: errorMessage };
    }
  }

  async onAuthStateChange(callback: (event: string, session: unknown) => void) {
    const supabase = await this.getSupabase();
    return supabase.auth.onAuthStateChange((event, session) => {
      callback(event, session);
    });
  }

  /**
   * Get current session for authenticated API calls
   * This method should ONLY be used by the api.ts module
   * Components should use authenticatedFetch from useAuth instead
   * @internal
   */
  async getSessionForAPI() {
    return this.getSession();
  }

  /**
   * Get current session - INTERNAL USE ONLY
   * This method is now private to prevent direct usage from components
   * @private
   */
  private async getSession() {
    const maxRetries = 3;
    let retryCount = 0;
    
    while (retryCount < maxRetries) {
      try {
        retryCount++;
        console.log(`🔄 [authService.getSession] Attempt ${retryCount}/${maxRetries}`);
        
        const supabase = await this.getSupabase();
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.warn(`⚠️ [authService.getSession] Error on attempt ${retryCount}:`, error);
          if (retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 500 * retryCount));
            continue;
          }
          throw error;
        }
        
        if (session) {
          console.log(`✅ [authService.getSession] Session retrieved successfully`);
          return session;
        }
        
        // No session but no error - might be a timing issue
        console.warn(`⚠️ [authService.getSession] No session found on attempt ${retryCount}`);
        if (retryCount < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 500 * retryCount));
          continue;
        }
        
        return null;
      } catch (error) {
        if (retryCount >= maxRetries) {
          console.error('❌ [authService.getSession] All retries exhausted:', error);
          return null;
        }
      }
    }
    
    console.error('❌ [authService.getSession] Failed to get session after all retries');
    return null;
  }
}

export const authService = new AuthService();
